import React, { ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import RoleProtectedRoute from './RoleProtectedRoute'

interface AdminProxyRouteGuardProps {
  children: ReactNode
}

// Maps the /admin/:type URL segment to the scoped sub-admin role (used by
// useRoleGuard's roleAccessMap) permitted to manage that proxy type.
const PROXY_TYPE_ROLE_MAP: Record<string, string> = {
  'approver-proxy': 'apAdmin',
  'float-proxy': 'feAdmin',
  'requestor-proxy': 'rpAdmin',
}

/**
 * Route guard for /admin/:type (ManageProxy). Grants access to full admins
 * plus the specific sub-admin role that owns the requested proxy type, so
 * e.g. an isAPAdmin-only user can reach /admin/approver-proxy directly but
 * not /admin/float-proxy or /admin/requestor-proxy.
 *
 * @param children - The protected ManageProxy content.
 * @returns RoleProtectedRoute configured with the roles allowed for this type.
 */
const AdminProxyRouteGuard: React.FC<AdminProxyRouteGuardProps> = ({ children }) => {
  const { type } = useParams()
  const roleForType = type ? PROXY_TYPE_ROLE_MAP[type] : undefined
  const allowedRoles = roleForType ? ['admin', roleForType] : ['admin']

  return <RoleProtectedRoute allowedRoles={allowedRoles}>{children}</RoleProtectedRoute>
}

export default AdminProxyRouteGuard
